export const TIME_ZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Halifax",
];

const enum REMINDER_PRIORITY {
  HIGH = "High",
  MEDIUM = "Medium",
  LOW = "Low",
}

const enum REMINDER_ROLE_TYPE {
  PROVIDER = "Provider",
  BUSINESS = "Business",
  CAREGIVER = "Caregiver",
  RESIDENT = "Resident",
  NURSE = "Nurse"
}

export class ReminderConstants {
  static FREQUENCY_EVERY_1_YEAR = "Every 1 year";
  static FREQUENCY_EVERY_2_YEARS = "Every 2 years";
  static FREQUENCY_EVERY_6_MONTHS = "Every 6 months";
  static FREQUENCY_QUARTERLY = "Quarterly";
  static FREQUENCY_EVERY_2_MONTHS = "Every 2 months (or 60 days)";
  static FREQUENCY_EVERY_MONTH = "Every month (or 30 days)";

  static EARLY_REMINDER_1_MONTH = "1 month early";
  static EARLY_REMINDER_2_MONTHS = "2 months early";
  static EARLY_REMINDER_3_MONTHS = "3 months early";
  static EARLY_REMINDER_1_WEEK = "1 week early";
  static EARLY_REMINDER_2_WEEKS = "2 weeks early";

  public static PRIORITY_OPTIONS = [REMINDER_PRIORITY.HIGH, REMINDER_PRIORITY.MEDIUM, REMINDER_PRIORITY.LOW];

  public static ROLE_OPTIONS = [REMINDER_ROLE_TYPE.PROVIDER, REMINDER_ROLE_TYPE.BUSINESS, REMINDER_ROLE_TYPE.CAREGIVER, REMINDER_ROLE_TYPE.NURSE, REMINDER_ROLE_TYPE.RESIDENT,];

  public static AFH_REMINDERS = [

    /* ================= PROVIDER COMPLIANCE ================= */

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Provider License Renewal",
      description: "Renew Adult Family Home license with DSHS.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_MONTHS,
      startReminderDateLabel: "License Issue Date",
      dueDateReminderLabel: "License Renewal Date",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Provider Continuing Education",
      description: "Complete required provider continuing education hours.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Training Date",
      dueDateReminderLabel: "Training Due Date",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Provider Background Check Renewal",
      description: "Renew provider background check.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_MONTHS,
      startReminderDateLabel: "Last Background Check",
      dueDateReminderLabel: "Next Background Check",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "AFH License Inspection Preparation",
      description: "Prepare documentation for licensing inspection.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Inspection",
      dueDateReminderLabel: "Expected Inspection",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Emergency Disaster Plan Review",
      description: "Review emergency disaster plan.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Emergency Drill",
      description: "Conduct emergency preparedness drill.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_6_MONTHS,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Drill",
      dueDateReminderLabel: "Next Drill",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Policies and Procedures Review",
      description: "Annual review of AFH policies.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.LOW
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Infection Control Plan Review",
      description: "Review infection control program.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Quality Assurance Review",
      description: "Internal quality assurance meeting.",
      recommendedFrequency: ReminderConstants.FREQUENCY_QUARTERLY,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Meeting",
      dueDateReminderLabel: "Next Meeting",
      priority: REMINDER_PRIORITY.LOW
    },

    {
      role: REMINDER_ROLE_TYPE.PROVIDER,
      title: "Resident Rights Policy Review",
      description: "Review resident rights policy.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.LOW
    },

    /* ================= BUSINESS FACILITY ================= */

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Liability Insurance Renewal",
      description: "Renew business liability insurance.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Policy Start",
      dueDateReminderLabel: "Policy Renewal",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Property Insurance Renewal",
      description: "Renew facility insurance coverage.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Policy Start",
      dueDateReminderLabel: "Policy Renewal",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Fire Drill",
      description: "Conduct fire evacuation drill.",
      recommendedFrequency: ReminderConstants.FREQUENCY_QUARTERLY,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Drill",
      dueDateReminderLabel: "Next Drill",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Fire Extinguisher Inspection",
      description: "Inspect fire extinguishers.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Inspection",
      dueDateReminderLabel: "Next Inspection",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Smoke Detector Testing",
      description: "Test smoke detectors.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_MONTH,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_WEEK,
      startReminderDateLabel: "Last Test",
      dueDateReminderLabel: "Next Test",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Carbon Monoxide Detector Testing",
      description: "Test carbon monoxide detectors.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_MONTH,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_WEEK,
      startReminderDateLabel: "Last Test",
      dueDateReminderLabel: "Next Test",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Emergency Lighting Check",
      description: "Inspect emergency lighting.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_6_MONTHS,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Inspection",
      dueDateReminderLabel: "Next Inspection",
      priority: REMINDER_PRIORITY.LOW
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Water Heater Temperature Check",
      description: "Verify water temperature safety limits.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_MONTH,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_WEEK,
      startReminderDateLabel: "Last Check",
      dueDateReminderLabel: "Next Check",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "HVAC Maintenance",
      description: "Service heating and cooling system.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Service",
      dueDateReminderLabel: "Next Service",
      priority: REMINDER_PRIORITY.LOW
    },

    {
      role: REMINDER_ROLE_TYPE.BUSINESS,
      title: "Pest Control Service",
      description: "Schedule pest control maintenance.",
      recommendedFrequency: ReminderConstants.FREQUENCY_QUARTERLY,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Service",
      dueDateReminderLabel: "Next Service",
      priority: REMINDER_PRIORITY.LOW
    },

    /* ================= CAREGIVER ================= */

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "CPR Certification Renewal",
      description: "Maintain valid CPR certification.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Certification Date",
      dueDateReminderLabel: "Renewal Date",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "First Aid Certification Renewal",
      description: "Maintain valid First Aid certification.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Certification Date",
      dueDateReminderLabel: "Renewal Date",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "Food Handler Permit Renewal",
      description: "Renew food handler permit.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Permit Issue",
      dueDateReminderLabel: "Permit Expiration",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "TB Test Renewal",
      description: "Maintain TB screening compliance.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Test",
      dueDateReminderLabel: "Next Test",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "Background Check Renewal",
      description: "Renew caregiver background check.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_MONTHS,
      startReminderDateLabel: "Last Check",
      dueDateReminderLabel: "Next Check",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "Caregiver Continuing Education",
      description: "Complete annual training hours.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Training",
      dueDateReminderLabel: "Training Due",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "Dementia Training",
      description: "Complete dementia specialty training.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Training",
      dueDateReminderLabel: "Next Training",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "Mental Health Training",
      description: "Complete mental health specialty training.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Training",
      dueDateReminderLabel: "Next Training",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.CAREGIVER,
      title: "Caregiver Performance Review",
      description: "Conduct annual caregiver performance review.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.LOW
    },

    /* ================= NURSE DELEGATION ================= */

    {
      role: REMINDER_ROLE_TYPE.NURSE,
      title: "Nurse Delegation Renewal",
      description: "Renew nurse delegation authorization.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Delegation Date",
      dueDateReminderLabel: "Renewal Date",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.NURSE,
      title: "Delegation Supervision Visit",
      description: "Nurse supervision visit.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_2_MONTHS,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Visit",
      dueDateReminderLabel: "Next Visit",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.NURSE,
      title: "Medication Competency Evaluation",
      description: "Evaluate caregiver medication competency.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Evaluation",
      dueDateReminderLabel: "Next Evaluation",
      priority: REMINDER_PRIORITY.HIGH
    },

    /* ================= RESIDENT ================= */

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Assessment",
      description: "Complete annual resident assessment.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Assessment",
      dueDateReminderLabel: "Next Assessment",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Negotiated Care Plan Review",
      description: "Update negotiated care plan.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Care Plan",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Medication Review",
      description: "Review medications with physician or pharmacist.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_6_MONTHS,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.HIGH
    },

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Weight Monitoring",
      description: "Monitor resident weight.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_MONTH,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_WEEK,
      startReminderDateLabel: "Last Weight",
      dueDateReminderLabel: "Next Weight",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Physician Visit",
      description: "Schedule physician checkup.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_6_MONTHS,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_2_WEEKS,
      startReminderDateLabel: "Last Visit",
      dueDateReminderLabel: "Next Visit",
      priority: REMINDER_PRIORITY.MEDIUM
    },

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Dental Exam",
      description: "Schedule dental exam.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Exam",
      dueDateReminderLabel: "Next Exam",
      priority: REMINDER_PRIORITY.LOW
    },

    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Vision Exam",
      description: "Schedule vision exam.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Exam",
      dueDateReminderLabel: "Next Exam",
      priority: REMINDER_PRIORITY.LOW
    },
    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Hearing Exam",
      description: "Schedule hearing exam.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Exam",
      dueDateReminderLabel: "Next Exam",
      priority: REMINDER_PRIORITY.LOW
    },
    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Foot Exam",
      description: "Schedule foot exam.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_1_YEAR,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Exam",
      dueDateReminderLabel: "Next Exam",
      priority: REMINDER_PRIORITY.LOW,
    },
    {
      role: REMINDER_ROLE_TYPE.RESIDENT,
      title: "Resident Admission Agreement Review",
      description: "Review resident admission agreement.",
      recommendedFrequency: ReminderConstants.FREQUENCY_EVERY_2_YEARS,
      recommendedEarlyReminder: ReminderConstants.EARLY_REMINDER_1_MONTH,
      startReminderDateLabel: "Last Review",
      dueDateReminderLabel: "Next Review",
      priority: REMINDER_PRIORITY.HIGH
    }
  ];


  public static REMINDER_FREQUENCY = [
    this.FREQUENCY_EVERY_MONTH,
    this.FREQUENCY_EVERY_2_MONTHS,
    this.FREQUENCY_QUARTERLY,
    this.FREQUENCY_EVERY_6_MONTHS,
    this.FREQUENCY_EVERY_1_YEAR,
    this.FREQUENCY_EVERY_2_YEARS,
  ];

  public static EARLY_REMINDER_WEEKS = [
    this.EARLY_REMINDER_1_WEEK,
    this.EARLY_REMINDER_2_WEEKS,
    this.EARLY_REMINDER_1_MONTH,
    this.EARLY_REMINDER_2_MONTHS,
    this.EARLY_REMINDER_3_MONTHS,
  ];

  public static convertEarlyReminderLabelToWeeks(label: string): number {
    switch (label) {
      case this.EARLY_REMINDER_1_WEEK:
        return 1;
      case this.EARLY_REMINDER_2_WEEKS:
        return 2;
      case this.EARLY_REMINDER_1_MONTH:
        return 4;
      case this.EARLY_REMINDER_2_MONTHS:
        return 8;
      case this.EARLY_REMINDER_3_MONTHS:
        return 12;
      default:
        return 0;
    }
  }
}

export enum REMINDER_TYPES {
  PREDEFINED_REMINDER = "Predefined Reminder",
  CUSTOM_REMINDER = "Custom Reminder",
}

type ReminderFrequency =
  | typeof ReminderConstants.FREQUENCY_EVERY_MONTH
  | typeof ReminderConstants.FREQUENCY_EVERY_2_MONTHS
  | typeof ReminderConstants.FREQUENCY_QUARTERLY
  | typeof ReminderConstants.FREQUENCY_EVERY_6_MONTHS
  | typeof ReminderConstants.FREQUENCY_EVERY_1_YEAR
  | typeof ReminderConstants.FREQUENCY_EVERY_2_YEARS;


export type ReminderDateOption = {
  frequency: ReminderFrequency;
  dueDateISO: string; // the cycle due date (anchor)
  early: number;
  reminderDateISO: string; // due - early.value weeks
};

function startOfDayLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + days);
  return x;
}

function addMonthsClamped(d: Date, months: number): Date {
  // Jan 31 + 1 month => Feb 28/29 (clamped)
  const year = d.getFullYear();
  const month = d.getMonth();
  const day = d.getDate();

  const targetMonthIndex = month + months;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;

  const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
  const clampedDay = Math.min(day, lastDay);
  return new Date(targetYear, targetMonth, clampedDay);
}

function monthsForFrequency(freq: ReminderFrequency): number {
  switch (freq) {
    case ReminderConstants.FREQUENCY_EVERY_MONTH:
      return 1;
    case ReminderConstants.FREQUENCY_EVERY_2_MONTHS:
      return 2;
    case ReminderConstants.FREQUENCY_QUARTERLY:
      return 3;
    case ReminderConstants.FREQUENCY_EVERY_6_MONTHS:
      return 6;
    case ReminderConstants.FREQUENCY_EVERY_1_YEAR:
      return 12;
    case ReminderConstants.FREQUENCY_EVERY_2_YEARS:
      return 24;
  }
  return 0; // default, should not happen
}

export function horizonMonthsForFrequency(freq: ReminderFrequency): number {
  switch (freq) {
    case ReminderConstants.FREQUENCY_EVERY_MONTH:
      return 6;
    case ReminderConstants.FREQUENCY_EVERY_2_MONTHS:
      return 12;
    case ReminderConstants.FREQUENCY_QUARTERLY:
      return 18;
    case ReminderConstants.FREQUENCY_EVERY_6_MONTHS:
      return 36;
    case ReminderConstants.FREQUENCY_EVERY_1_YEAR:
      return 72;
    case ReminderConstants.FREQUENCY_EVERY_2_YEARS:
      return 144;
  }
  return 0; // default, should not happen
}

export class ReminderDateOptionParams {
  startDate: Date | undefined; // required baseline (e.g., policy start date, rule start date)
  dueDate?: Date | undefined; // optional (null/undefined means recurring no due date)
  frequency: ReminderFrequency | undefined; // e.g., "Quarterly"
  earlyReminderOptions?: number[];
  // For "no due date" recurring rules, how far ahead to generate due dates (default 18 months)
  horizonMonths?: number;

  // If dueDate is provided, how far back (in months) to generate prior cycle due dates.
  // Example: Annual due date with lookbackMonths=36 => due, due-12m, due-24m, due-36m
  lookbackMonths?: number;

  // Filtering / safety
  futureOnly?: boolean; // default false
  minDate?: Date | string;
  maxDate?: Date | string;
}

/**
 * Generates reminder trigger dates.
 *
 * Two modes:
 * 1) With dueDate: generates reminders relative to that due date, AND (optionally) prior cycle due dates.
 * 2) No dueDate (e.g., Fire Drill): generates a rolling schedule of cycle due dates for a horizon window.
 */
export function generateReminderDateOptions(params: ReminderDateOptionParams): ReminderDateOption[] {
  const start = new Date(params.startDate!);
  const due = params.dueDate == null ? null : new Date(params.dueDate);

  const freq = params.frequency;
  const stepMonths = monthsForFrequency(freq!);
  const earlyOpts = params.earlyReminderOptions;

  const horizonMonths = params.horizonMonths ?? 18;
  const lookbackMonths = params.lookbackMonths ?? 12;
  const futureOnly = params.futureOnly ?? false;

  const now = startOfDayLocal(new Date());

  const minDate =
    params.minDate == null
      ? null
      : startOfDayLocal(typeof params.minDate === "string" ? new Date(params.minDate) : params.minDate);

  const maxDate =
    params.maxDate == null
      ? null
      : startOfDayLocal(typeof params.maxDate === "string" ? new Date(params.maxDate) : params.maxDate);

  // Build cycle due dates (anchors)
  const dueDates: Date[] = [];

  if (due) {
    // Anchors go backwards from due date in steps until lookbackMonths reached
    for (let k = 0; ; k++) {
      const monthsBack = k * stepMonths;
      if (monthsBack > lookbackMonths) break;
      const anchor = addMonthsClamped(due, -monthsBack);
      // avoid anchors before start date (optional; remove if you want historical)
      if (anchor < start) continue;
      dueDates.push(anchor);
    }
  } else {
    // No due date: create rolling schedule forward from start for horizonMonths
    const end = addMonthsClamped(start, horizonMonths);
    for (let k = 0; ; k++) {
      const anchor = addMonthsClamped(start, k * stepMonths);
      if (anchor > end) break;
      dueDates.push(anchor);
    }
  }

  const results: ReminderDateOption[] = [];

  for (const dueDate of dueDates) {
    for (const early of earlyOpts!) {
      const reminderDate = addDays(dueDate, -(early * 7));

      // Don’t schedule reminders before the rule start date
      if (reminderDate < start) continue;

      if (futureOnly && reminderDate < now) continue;
      if (minDate && reminderDate < minDate) continue;
      if (maxDate && reminderDate > maxDate) continue;

      results.push({
        frequency: freq!,
        dueDateISO: dueDate.toDateString(),
        early,
        reminderDateISO: reminderDate.toDateString(),
      });
    }
  }

  // // Sort ascending by reminder date
  // results.sort((a, b) => {
  //   const c = a.reminderDateISO.localeCompare(b.reminderDateISO);
  //   return c == 0 ? c : a.dueDateISO.localeCompare(b.dueDateISO);
  // });

  // Deduplicate exact duplicates
  const seen = new Set<string>();
  const deduped: ReminderDateOption[] = [];
  for (const r of results) {
    const key = `${r.frequency}|${r.dueDateISO}|${r.early}|${r.reminderDateISO}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(r);
  }

  return deduped;
}
